import { type FC, useEffect, useState } from 'react';
import Button from './Button';
import { LAYOUT_ALGORITHMS, type LayoutAlgorithm, type LayoutDirection } from '@/services/autoLayout';

interface LayoutPickerDialogProps {
  open: boolean;
  defaultAlgorithm?: LayoutAlgorithm;
  defaultDirection?: LayoutDirection;
  busy?: boolean;
  onConfirm: (algorithm: LayoutAlgorithm, direction: LayoutDirection) => void;
  onCancel: () => void;
}

const DIRECTIONS: { value: LayoutDirection; label: string }[] = [
  { value: 'TB', label: 'Haut → Bas' },
  { value: 'LR', label: 'Gauche → Droite' },
  { value: 'BT', label: 'Bas → Haut' },
  { value: 'RL', label: 'Droite → Gauche' },
];

const ALGORITHMS_WITH_DIRECTION: LayoutAlgorithm[] = ['layered', 'mrtree'];

export const LayoutPickerDialog: FC<LayoutPickerDialogProps> = ({
  open,
  defaultAlgorithm = 'layered',
  defaultDirection = 'TB',
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const [algorithm, setAlgorithm] = useState<LayoutAlgorithm>(defaultAlgorithm);
  const [direction, setDirection] = useState<LayoutDirection>(defaultDirection);

  useEffect(() => {
    if (open) {
      setAlgorithm(defaultAlgorithm);
      setDirection(defaultDirection);
    }
  }, [open, defaultAlgorithm, defaultDirection]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const showDirection = ALGORITHMS_WITH_DIRECTION.includes(algorithm);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={() => !busy && onCancel()}
    >
      <div
        style={{
          minWidth: 480,
          maxWidth: 560,
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-lg)',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 600,
            color: 'var(--color-text)',
            marginBottom: 'var(--spacing-xs)',
          }}
        >
          Réorganiser le canevas
        </h3>
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            marginBottom: 'var(--spacing-md)',
          }}
        >
          Choisissez la méthode de mise en page à appliquer.
        </p>

        <div
          role="radiogroup"
          aria-label="Algorithme de mise en page"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-xs)',
            marginBottom: 'var(--spacing-md)',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {LAYOUT_ALGORITHMS.map((algo) => {
            const selected = algorithm === algo.value;
            return (
              <label
                key={algo.value}
                style={{
                  display: 'flex',
                  gap: 'var(--spacing-sm)',
                  padding: 'var(--spacing-sm)',
                  border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  backgroundColor: selected ? 'var(--color-background)' : 'transparent',
                }}
              >
                <input
                  type="radio"
                  name="layout-algorithm"
                  value={algo.value}
                  checked={selected}
                  onChange={() => setAlgorithm(algo.value)}
                  style={{ marginTop: 3 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 600,
                      color: 'var(--color-text)',
                    }}
                  >
                    {algo.label}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      lineHeight: 1.4,
                    }}
                  >
                    {algo.description}
                  </span>
                </div>
              </label>
            );
          })}
        </div>

        {showDirection && (
          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <label
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Orientation
            </label>
            <div style={{ display: 'flex', gap: 'var(--spacing-xs)', marginTop: 6, flexWrap: 'wrap' }}>
              {DIRECTIONS.map((d) => {
                const selected = direction === d.value;
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setDirection(d.value)}
                    style={{
                      padding: '6px 10px',
                      border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: selected ? 'var(--color-background)' : 'transparent',
                      color: 'var(--color-text)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                    }}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--spacing-sm)',
          }}
        >
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(algorithm, direction)}
            isLoading={busy}
          >
            Appliquer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LayoutPickerDialog;
